
import { useEffect, useRef, useCallback } from "react";
import { useSceneObjects } from "../hooks/useSceneObjects";
import { useUiStore } from "../stores/uiStore";
import { useDragPreview } from "../hooks/useDragPreview";
import { useEditMode } from "../hooks/useEditMode";
import type { SceneObject } from "@mind-sands/shared";
import {
  setDragScreenPosition,
  setDragCanvasRect,
  isOverCanvas,
} from "../engine/dragState";

export function DragController() {
  const { dragPreview, setDragPreview } = useDragPreview();
  const { addObject } = useSceneObjects();
  const { isReadonly } = useEditMode();
  const canvasContainerRef = useRef<HTMLDivElement | null>(null);

  const updateCanvasRect = useCallback(() => {
    // Re-query on each call: the canvas container is lazy-loaded (Suspense)
    // and may not exist when this component first mounts.
    if (!canvasContainerRef.current) {
      const container = document.querySelector(".sandbox-canvas-container");
      if (container) {
        canvasContainerRef.current = container as HTMLDivElement;
      }
    }
    if (canvasContainerRef.current) {
      setDragCanvasRect(canvasContainerRef.current.getBoundingClientRect());
    }
  }, []);

  useEffect(() => {
    if (!dragPreview || isReadonly) return;

    updateCanvasRect();

    const handleMouseMove = (e: MouseEvent) => {
      setDragScreenPosition(e.clientX, e.clientY);
      updateCanvasRect();
    };

    const handleMouseUp = (e: MouseEvent) => {
      setDragScreenPosition(e.clientX, e.clientY);
      updateCanvasRect();

      const currentPreview = useUiStore.getState().dragPreview;
      if (currentPreview && currentPreview.position && isOverCanvas()) {
        const newObject: SceneObject = {
          id: `obj-${crypto.randomUUID()}`,
          objectId: currentPreview.objectId,
          position: currentPreview.position,
          scale: 1,
          rotation: [0, 0, 0],
        };
        void addObject(newObject);
      }

      setDragPreview(null);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setDragPreview(null);
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", updateCanvasRect);
    window.addEventListener("scroll", updateCanvasRect, true);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", updateCanvasRect);
      window.removeEventListener("scroll", updateCanvasRect, true);
    };
  }, [dragPreview, setDragPreview, addObject, updateCanvasRect, isReadonly]);

  return null;
}
